/**
 * SettingsPanel.tsx —— 局中设置（PRD 模块 3 / 5.1，全部即时生效且持久化）
 *
 * 只放**不影响牌局逻辑**的偏好项：改这里不会破坏「同种子可复现」。
 * 影响牌局的开局参数（人数 / 盲注 / 带入 / 种子）只在开局设置里，局中只读展示。
 */

import type { Game } from '../core/game';
import { DEBUG_TOOLS } from '../state/env';
import { chips, styleName } from '../state/format';
import type { Settings } from '../state/settings';
import { Segmented, Switch } from './primitives';
import type { StyleKey } from '../core/ai';

const QUICK_PRESETS: readonly { label: string; value: readonly number[] }[] = [
  { label: '½ · ¾ · 1× · 2×', value: [0.5, 0.75, 1, 2] },
  { label: '⅓ · ½ · ¾ · 1×', value: [0.34, 0.5, 0.75, 1] },
  { label: '½ · 1× · 2× · 3×', value: [0.5, 1, 2, 3] },
];

export function SettingsPanel({
  settings,
  game,
  styles,
  equityDisabledReason,
  onPatch,
  onSpeed,
  onEquityEnabled,
  onExitSave,
  onQuitToHome,
}: {
  settings: Settings;
  game: Game | null;
  styles: Record<number, StyleKey>;
  equityDisabledReason: string | null;
  onPatch: (p: Partial<Settings>) => void;
  onSpeed: (s: 1 | 2 | 4) => void;
  onEquityEnabled: (on: boolean) => void;
  onExitSave: () => void;
  onQuitToHome: () => void;
}) {
  return (
    <div className="setpanel">
      <section className="setpanel__group">
        <h3 className="setpanel__title">节奏</h3>
        <div className="setpanel__row">
          <span>牌局速度</span>
          <Segmented
            ariaLabel="牌局速度"
            value={settings.speed}
            onChange={(v) => onSpeed(v)}
            options={[
              { value: 1 as const, label: '1×' },
              { value: 2 as const, label: '2×' },
              { value: 4 as const, label: '4×' },
            ]}
          />
        </div>
        <div className="setpanel__row">
          <span>行动时限</span>
          <Segmented
            ariaLabel="行动时限"
            value={settings.actionClock}
            onChange={(v) => onPatch({ actionClock: v })}
            options={[
              { value: 0 as const, label: '关闭' },
              { value: 15 as const, label: '15s' },
              { value: 30 as const, label: '30s' },
            ]}
          />
        </div>
        <p className="setpanel__hint">开启时限后，超时会自动过牌（无人下注时）或弃牌；赢率计算进行中倒计时会暂停。</p>
        <Switch
          checked={settings.autoContinue}
          onChange={(v) => onPatch({ autoContinue: v })}
          label="自动继续下一手"
          hint="默认关闭：结算后停留，点「继续 / 下一手」或按 Enter/空格 推进。开启后非摊牌手停留 ≥5s 自动续手（可随时提前）；摊牌手仍需手动继续。"
        />
        {/* R13-2：弃牌后二选一推进的默认与「每次询问」开关。turbo 只压缩本手剩余节奏，
            不写 speed、不改 AI 决策/随机序/结算；摊牌手无论何模式都沿用「手动等待」。 */}
        <div className="setpanel__row">
          <span>弃牌后推进</span>
          <Segmented
            ariaLabel="弃牌后推进"
            value={settings.foldAdvanceMode}
            onChange={(v) => onPatch({ foldAdvanceMode: v })}
            options={[
              { value: 'normal' as const, label: '原速观战' },
              { value: 'turbo' as const, label: '快进本手' },
            ]}
          />
        </div>
        <Switch
          checked={settings.foldAdvanceAsk}
          onChange={(v) => onPatch({ foldAdvanceAsk: v })}
          label="弃牌后每次询问"
          hint="开启后每次弃牌都弹出「快进 / 原速」二选一；关闭后直接按上面的默认执行。快进仅加速本手剩余进程，不改变发牌、对手决策与结算，摊牌手仍需手动继续。"
        />
      </section>

      <section className="setpanel__group">
        <h3 className="setpanel__title">赢率与评分</h3>
        <Switch
          checked={settings.equityEnabled}
          disabled={!!equityDisabledReason}
          onChange={onEquityEnabled}
          label="开启赢率计算器"
          hint={equityDisabledReason ?? '默认关闭。开启后在独立线程精确枚举，不影响操作。'}
        />
        <div className="setpanel__row">
          <span>计算模式</span>
          <Segmented
            ariaLabel="计算模式"
            value={settings.equityMode}
            disabled={!settings.equityEnabled}
            onChange={(v) => onPatch({ equityMode: v })}
            options={[
              { value: 'live' as const, label: '实时' },
              { value: 'review' as const, label: '复盘对照' },
            ]}
          />
        </div>
        <Switch checked={settings.scoreEnabled} onChange={(v) => onPatch({ scoreEnabled: v })} label="行动后显示决策评分" />
        {/* 需求二 FR2-2：语气模式二选一（新手鼓励 / 专业直述），持久化、跨手跨会话保持；
            评分总开关关闭时置灰保留（AC2-7）。切换即时生效于下一条评分（AC2-6 不改判定与数值）。 */}
        <div className="setpanel__row">
          <span>语气模式</span>
          <Segmented
            ariaLabel="决策评分语气模式"
            value={settings.scoreToneMode}
            disabled={!settings.scoreEnabled}
            onChange={(v) => onPatch({ scoreToneMode: v })}
            options={[
              { value: 'beginner' as const, label: '新手鼓励' },
              { value: 'pro' as const, label: '专业直述' },
            ]}
          />
        </div>
        <p className="setpanel__hint">
          新手鼓励：评级用鼓励措辞、无红字打击；专业直述：保留「最优 / 可接受 / 失误 / 严重失误 +
          少赢 X% 底池」量化直评。两档只改措辞与配色，不改评级档位与 EV 差数值。
        </p>
        <Switch checked={settings.revealFolded} onChange={(v) => onPatch({ revealFolded: v })} label="复盘时显示弃牌者底牌" />
        {/* 慢速开关是调试能力，生产包里整块被摇树移除（PRD 8.6） */}
        {DEBUG_TOOLS ? (
          <Switch
            checked={settings.debugSlowEquity}
            onChange={(v) => onPatch({ debugSlowEquity: v })}
            label="调试：拖慢赢率计算"
            hint="仅用于观察加载态与慢速提示态，不改变任何数值口径。"
          />
        ) : null}
      </section>

      <section className="setpanel__group">
        <h3 className="setpanel__title">操作与显示</h3>
        <Switch checked={settings.fourColor} onChange={(v) => onPatch({ fourColor: v })} label="四色牌面" hint="黑桃黑 / 红桃红 / 方块蓝 / 梅花绿" />
        <Switch checked={settings.allInConfirm} onChange={(v) => onPatch({ allInConfirm: v })} label="全下与大额加注二次确认" />
        <div className="setpanel__row">
          <span>动效</span>
          <Segmented
            ariaLabel="动效"
            value={settings.motion}
            onChange={(v) => onPatch({ motion: v })}
            options={[
              { value: 'system' as const, label: '跟随系统' },
              { value: 'on' as const, label: '开启' },
              { value: 'off' as const, label: '关闭' },
            ]}
          />
        </div>
        <div className="setpanel__row setpanel__row--col">
          <span>快捷下注档位</span>
          <div className="setpanel__quick">
            {QUICK_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className={`chipbtn${sameRatios(settings.quickBets, p.value) ? ' chipbtn--on' : ''}`}
                onClick={() => onPatch({ quickBets: [...p.value] })}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="setpanel__hint">档位按「跟注后底池」计算，选择后立即在行动区生效。</p>
        </div>
      </section>

      {game ? (
        <section className="setpanel__group">
          <h3 className="setpanel__title">本局信息（只读）</h3>
          <dl className="kv kv--tight">
            <div>
              <dt>桌型</dt>
              <dd>
                {game.config.seatCount} 人 · 盲注{' '}
                <span className="num">
                  {game.config.smallBlind}/{game.config.bigBlind}
                </span>
                {game.config.ante ? <span className="num"> · 前注 {game.config.ante}</span> : null}
              </dd>
            </div>
            <div>
              <dt>带入筹码</dt>
              <dd className="num">{chips(game.config.startingStack)}</dd>
            </div>
            <div>
              <dt>随机种子</dt>
              <dd className="num">{game.userSeed}</dd>
            </div>
            <div>
              <dt>AI 风格</dt>
              <dd>
                {Object.keys(styles)
                  .map(Number)
                  .sort((a, b) => a - b)
                  .map((s) => `${game.players[s]?.name ?? s}：${styleName(styles[s])}`)
                  .join(' · ')}
              </dd>
            </div>
          </dl>
          <p className="setpanel__hint">开局参数在对局中不可更改，避免破坏「同种子可复现」。</p>
        </section>
      ) : null}

      <section className="setpanel__group">
        <h3 className="setpanel__title">退出</h3>
        <div className="setpanel__actions">
          <button type="button" className="btn" onClick={onExitSave}>
            保存并退出到首页
          </button>
          <button type="button" className="btn btn--danger" onClick={onQuitToHome}>
            放弃本局并回到首页
          </button>
        </div>
        <p className="setpanel__hint">每手结束都会自动保存，刷新页面可从首页「继续上次对局」恢复。</p>
      </section>
    </div>
  );
}

function sameRatios(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((v, i) => Math.abs(v - b[i]) < 1e-6);
}
